import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FileText, Receipt, MessageSquare, Clipboard, Check, Building2,
  Mail, Phone, Calendar, Euro, Clock, AlertCircle, CheckCircle,
  Send, X, Plus, ChevronRight, Star, Zap, Users, Bot, Headphones
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import SignatureCanvas from 'react-signature-canvas';

import { API_URL } from '../../config/api';

const statusLabels = {
  draft: { label: 'Brouillon', color: 'bg-gray-100 text-gray-600', icon: FileText },
  sent: { label: 'En attente de signature', color: 'bg-blue-100 text-blue-600', icon: Clock },
  accepted: { label: 'Accepté', color: 'bg-green-100 text-green-600', icon: CheckCircle },
  refused: { label: 'Refusé', color: 'bg-red-100 text-red-600', icon: X },
  expired: { label: 'Expiré', color: 'bg-amber-100 text-amber-600', icon: AlertCircle },
};

const invoiceStatusLabels = {
  draft: { label: 'Brouillon', color: 'bg-gray-100 text-gray-600' },
  sent: { label: 'Envoyée', color: 'bg-blue-100 text-blue-600' },
  paid: { label: 'Payée', color: 'bg-green-100 text-green-600' },
  overdue: { label: 'En retard', color: 'bg-red-100 text-red-600' },
};

const ticketStatusLabels = {
  open: { label: 'Ouvert', color: 'bg-blue-100 text-blue-600' },
  in_progress: { label: 'En cours', color: 'bg-amber-100 text-amber-600' },
  resolved: { label: 'Résolu', color: 'bg-green-100 text-green-600' },
  closed: { label: 'Fermé', color: 'bg-gray-100 text-gray-600' },
};

// Offer details for display
const offerDetails = {
  'Essentiel': {
    icon: Clock,
    color: 'from-blue-500 to-blue-600',
    highlights: [
      { icon: Users, text: 'Assistant mi-temps (4h/jour)' },
      { icon: Bot, text: '7 Agents IA inclus' },
      { icon: Headphones, text: 'Support prioritaire' },
    ],
    features: ['Gestion administrative', 'Prise de rendez-vous', 'Relances clients', 'CRM Bâtiment inclus', 'Formation initiale', 'Suivi hebdomadaire']
  },
  'Business': {
    icon: Zap,
    color: 'from-purple-500 to-purple-600',
    highlights: [
      { icon: Users, text: 'Assistant temps plein (40h/sem)' },
      { icon: Bot, text: '7 Agents IA inclus' },
      { icon: Star, text: 'Support VIP' },
    ],
    features: ['Tout le forfait Essentiel', 'Prospection commerciale', 'Gestion des devis', 'Suivi chantiers', 'Reporting mensuel', 'Accompagnement stratégique']
  },
  'Premium': {
    icon: Star,
    color: 'from-amber-500 to-amber-600',
    highlights: [
      { icon: Users, text: '2 Assistants dédiés' },
      { icon: Bot, text: '7 Agents IA + personnalisés' },
      { icon: Star, text: 'Conciergerie business' },
    ],
    features: ['Tout le forfait Business', 'Assistant général + prospection', 'Personnalisation des agents IA', 'Intégration outils métier', 'Reporting avancé', 'Ligne directe manager']
  }
};

export default function ProspectPortalPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState('quote');
  const [showSignModal, setShowSignModal] = useState(false);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [ticketForm, setTicketForm] = useState({ subject: '', message: '', category: 'question' });
  const signatureRef = useRef(null);

  useEffect(() => {
    fetchPortalData();
  }, [token]);

  const fetchPortalData = async () => {
    try {
      const response = await fetch(`${API_URL}/api/prospect-portal/${token}`);
      if (!response.ok) {
        throw new Error('Lien invalide ou expiré');
      }
      const result = await response.json();
      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSign = async () => {
    if (!signatureRef.current || signatureRef.current.isEmpty()) {
      toast.error('Veuillez signer avant de valider');
      return;
    }

    const signatureData = signatureRef.current.toDataURL();
    
    try {
      const response = await fetch(`${API_URL}/api/prospect-portal/${token}/sign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ signature_data: signatureData })
      });
      
      if (response.ok) {
        toast.success('Devis signé avec succès ! Merci pour ta confiance.');
        setShowSignModal(false);
        fetchPortalData();
      } else {
        toast.error('Erreur lors de la signature');
      }
    } catch (err) {
      toast.error('Erreur de connexion');
    }
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    if (!ticketForm.subject || !ticketForm.message) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/prospect-portal/${token}/tickets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(ticketForm)
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(`Ticket ${result.ticket_number} créé`);
        setShowTicketModal(false);
        setTicketForm({ subject: '', message: '', category: 'question' });
        fetchPortalData();
      } else {
        toast.error('Erreur lors de la création du ticket');
      }
    } catch (err) {
      toast.error('Erreur de connexion');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-white"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 text-center max-w-md">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Lien invalide</h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  const { quote, invoices, notes, tickets, contact } = data;
  const quoteStatus = statusLabels[quote?.status] || statusLabels.draft;
  const StatusIcon = quoteStatus.icon;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900" data-testid="prospect-portal">
      {/* Header */}
      <header className="bg-white/10 backdrop-blur-md border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                <span className="text-blue-600 font-bold text-lg">R</span>
              </div>
              <div>
                <h1 className="text-white font-bold">BatiAssist</h1>
                <p className="text-white/60 text-sm">Espace Prospect</p>
              </div>
            </div>
            <div className="text-right text-white">
              <p className="font-medium">{contact?.name}</p>
              <p className="text-sm text-white/60">{contact?.company}</p>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <nav className="bg-white/5 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex gap-1">
            {[
              { id: 'quote', label: 'Mon Devis', icon: FileText },
              { id: 'invoices', label: 'Factures', icon: Receipt, count: invoices?.length },
              { id: 'notes', label: 'Notes', icon: Clipboard, count: notes?.length },
              { id: 'tickets', label: 'Mes Demandes', icon: MessageSquare, count: tickets?.length },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-3 flex items-center gap-2 text-sm font-medium transition-colors border-b-2 ${
                  activeTab === tab.id 
                    ? 'text-white border-blue-400' 
                    : 'text-white/60 border-transparent hover:text-white'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
                {tab.count > 0 && (
                  <span className="bg-blue-500 text-white text-xs px-1.5 py-0.5 rounded-full">{tab.count}</span>
                )}
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-6xl mx-auto px-4 py-8">
        <AnimatePresence mode="wait">
          {/* Quote Tab */}
          {activeTab === 'quote' && (
            <motion.div
              key="quote"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-6"
            >
              {/* Quote Header */}
              <div className="bg-white rounded-2xl p-6 shadow-xl">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <h2 className="text-2xl font-bold text-gray-900">{quote?.quote_number}</h2>
                      <span className={`flex items-center gap-1 text-sm px-3 py-1 rounded-full ${quoteStatus.color}`}>
                        <StatusIcon className="w-4 h-4" />
                        {quoteStatus.label}
                      </span>
                    </div>
                    <p className="text-gray-500">Créé le {formatDate(quote?.created_at)}</p>
                  </div>
                  {(quote?.status === 'draft' || quote?.status === 'sent') && !quote?.signed && (
                    <Button 
                      onClick={() => setShowSignModal(true)}
                      className="bg-green-600 hover:bg-green-700"
                      data-testid="sign-quote-btn"
                    >
                      <Check className="w-4 h-4 mr-2" />
                      Signer le devis
                    </Button>
                  )}
                  {quote?.signed && (
                    <div className="flex items-center gap-2 text-green-600 bg-green-50 px-4 py-2 rounded-lg">
                      <CheckCircle className="w-5 h-5" />
                      <span className="font-medium">Signé le {formatDate(quote?.signed_at)}</span>
                    </div>
                  )}
                </div>

                {/* Validity */}
                {quote?.valid_until && quote?.status !== 'accepted' && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-6">
                    <div className="flex items-center gap-2 text-amber-700">
                      <Clock className="w-4 h-4" />
                      <span>Ce devis est valable jusqu'au <strong>{formatDate(quote?.valid_until)}</strong></span>
                    </div>
                  </div>
                )}

                {/* Contact Info */}
                <div className="grid md:grid-cols-2 gap-6 mb-8">
                  <div className="bg-gray-50 rounded-xl p-4">
                    <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <Building2 className="w-4 h-4" />
                      Vos informations
                    </h3>
                    <div className="space-y-2 text-sm">
                      <p><span className="text-gray-500">Nom :</span> {quote?.contact_name}</p>
                      {quote?.company_name && <p><span className="text-gray-500">Entreprise :</span> {quote?.company_name}</p>}
                      <p><span className="text-gray-500">Email :</span> {quote?.contact_email}</p>
                      {quote?.contact_phone && <p><span className="text-gray-500">Tél :</span> {quote?.contact_phone}</p>}
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-4">
                    <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      Dates
                    </h3>
                    <div className="space-y-2 text-sm">
                      <p><span className="text-gray-500">Créé le :</span> {formatDate(quote?.created_at)}</p>
                      <p><span className="text-gray-500">Valide jusqu'au :</span> {formatDate(quote?.valid_until)}</p>
                      {quote?.signed_at && <p><span className="text-gray-500">Signé le :</span> {formatDate(quote?.signed_at)}</p>}
                    </div>
                  </div>
                </div>

                {/* Items - Beautiful offer cards */}
                <h3 className="font-semibold text-gray-900 mb-4">Forfait proposé</h3>
                <div className="space-y-4 mb-6">
                  {quote?.items?.map((item, index) => {
                    const details = offerDetails[item.offer_name] || {};
                    const Icon = details.icon || Star;
                    
                    return (
                      <div key={index} className="border border-gray-200 rounded-2xl overflow-hidden">
                        <div className={`bg-gradient-to-br ${details.color || 'from-gray-500 to-gray-600'} p-6 text-white`}>
                          <div className="flex items-start justify-between">
                            <div>
                              <div className="flex items-center gap-2 mb-2">
                                <Icon className="w-6 h-6" />
                                <h4 className="text-xl font-bold">{item.offer_name}</h4>
                              </div>
                              <div className="flex items-baseline gap-1">
                                <span className="text-3xl font-bold">{formatCurrency(item.price).replace('€', '').trim()}</span>
                                <span className="text-white/80">€/mois</span>
                              </div>
                            </div>
                            <div className="text-right">
                              <span className="bg-white/20 px-3 py-1 rounded-full text-sm">
                                x{item.quantity}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="p-6 bg-white">
                          {/* Highlights */}
                          <div className="grid md:grid-cols-3 gap-4 mb-4">
                            {details.highlights?.map((h, i) => (
                              <div key={i} className="flex items-center gap-2 text-sm">
                                <h.icon className="w-4 h-4 text-gray-500" />
                                <span className="font-medium">{h.text}</span>
                              </div>
                            ))}
                          </div>
                          {/* Features */}
                          <div className="grid md:grid-cols-2 gap-2 pt-4 border-t border-gray-100">
                            {details.features?.map((f, i) => (
                              <div key={i} className="flex items-center gap-2 text-sm text-gray-600">
                                <Check className="w-4 h-4 text-green-500" />
                                <span>{f}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Totals */}
                <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6">
                  <div className="space-y-3">
                    <div className="flex justify-between text-gray-600">
                      <span>Sous-total HT</span>
                      <span>{formatCurrency(quote?.subtotal)}</span>
                    </div>
                    {quote?.discount_amount > 0 && (
                      <div className="flex justify-between text-green-600">
                        <span>Remise</span>
                        <span>-{formatCurrency(quote?.discount_amount)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-gray-600">
                      <span>TVA ({quote?.tax_rate}%)</span>
                      <span>{formatCurrency(quote?.tax_amount)}</span>
                    </div>
                    <div className="flex justify-between text-2xl font-bold pt-3 border-t border-gray-200">
                      <span>Total TTC</span>
                      <span className="text-blue-600">{formatCurrency(quote?.total)}/mois</span>
                    </div>
                  </div>
                </div>

                {/* Notes */}
                {quote?.notes && (
                  <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                    <h4 className="font-medium text-blue-900 mb-2">Notes</h4>
                    <p className="text-blue-800">{quote?.notes}</p>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* Invoices Tab */}
          {activeTab === 'invoices' && (
            <motion.div
              key="invoices"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                <div className="p-6 border-b border-gray-100">
                  <h2 className="text-xl font-bold text-gray-900">Mes Factures</h2>
                </div>
                {invoices?.length === 0 ? (
                  <div className="p-12 text-center text-gray-500">
                    <Receipt className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p>Aucune facture pour le moment</p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {invoices?.map(invoice => {
                      const status = invoiceStatusLabels[invoice.status] || invoiceStatusLabels.draft;
                      return (
                        <div key={invoice.id} className="p-4 hover:bg-gray-50">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                                <Receipt className="w-5 h-5 text-blue-600" />
                              </div>
                              <div>
                                <p className="font-medium text-gray-900">{invoice.invoice_number}</p>
                                <p className="text-sm text-gray-500">{formatDate(invoice.issue_date)}</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="font-semibold text-gray-900">{formatCurrency(invoice.total)}</p>
                              <span className={`text-xs px-2 py-1 rounded-full ${status.color}`}>
                                {status.label}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* Notes Tab */}
          {activeTab === 'notes' && (
            <motion.div
              key="notes"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                <div className="p-6 border-b border-gray-100">
                  <h2 className="text-xl font-bold text-gray-900">Notes de ton conseiller</h2>
                </div>
                {notes?.length === 0 ? (
                  <div className="p-12 text-center text-gray-500">
                    <Clipboard className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p>Aucune note pour le moment</p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {notes?.map(note => (
                      <div key={note.id} className="p-4">
                        <p className="text-gray-700">{note.content}</p>
                        <p className="text-xs text-gray-400 mt-2">{formatDate(note.created_at)}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* Tickets Tab */}
          {activeTab === 'tickets' && (
            <motion.div
              key="tickets"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                <div className="p-6 border-b border-gray-100 flex items-center justify-between">
                  <h2 className="text-xl font-bold text-gray-900">Mes Demandes</h2>
                  <Button 
                    onClick={() => setShowTicketModal(true)}
                    className="bg-blue-600 hover:bg-blue-700"
                    data-testid="create-ticket-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Nouvelle demande
                  </Button>
                </div>
                {tickets?.length === 0 ? (
                  <div className="p-12 text-center text-gray-500">
                    <MessageSquare className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p>Aucune demande pour le moment</p>
                    <Button 
                      onClick={() => setShowTicketModal(true)}
                      className="mt-4 bg-blue-600"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Créer une demande
                    </Button>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {tickets?.map(ticket => {
                      const status = ticketStatusLabels[ticket.status] || ticketStatusLabels.open;
                      return (
                        <div key={ticket.id} className="p-4 hover:bg-gray-50">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                                <MessageSquare className="w-5 h-5 text-purple-600" />
                              </div>
                              <div>
                                <p className="font-medium text-gray-900">{ticket.subject}</p>
                                <p className="text-sm text-gray-500">{ticket.ticket_number} • {formatDate(ticket.created_at)}</p>
                              </div>
                            </div>
                            <span className={`text-xs px-2 py-1 rounded-full ${status.color}`}>
                              {status.label}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* Sign Modal */}
      <AnimatePresence>
        {showSignModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowSignModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl w-full max-w-lg shadow-xl"
              onClick={(e) => e.stopPropagation()}
              data-testid="sign-modal"
            >
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold">Signer le devis</h2>
                  <button onClick={() => setShowSignModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
              
              <div className="p-6">
                <p className="text-gray-600 mb-4">
                  En signant ce devis, tu acceptes les conditions et le forfait <strong>{quote?.items?.[0]?.offer_name}</strong> à <strong>{formatCurrency(quote?.total)}/mois</strong>.
                </p>
                
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-2 mb-4">
                  <p className="text-sm text-gray-500 mb-2">Ta signature :</p>
                  <SignatureCanvas
                    ref={signatureRef}
                    canvasProps={{
                      className: 'w-full h-40 bg-gray-50 rounded',
                      style: { width: '100%', height: '160px' }
                    }}
                  />
                </div>
                
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => signatureRef.current?.clear()}
                    className="flex-1"
                  >
                    Effacer
                  </Button>
                  <Button
                    onClick={handleSign}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                    data-testid="confirm-sign-btn"
                  >
                    <Check className="w-4 h-4 mr-2" />
                    Signer
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Create Ticket Modal */}
      <AnimatePresence>
        {showTicketModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowTicketModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl w-full max-w-lg shadow-xl"
              onClick={(e) => e.stopPropagation()}
              data-testid="ticket-modal"
            >
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold">Nouvelle demande</h2>
                  <button onClick={() => setShowTicketModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
              
              <form onSubmit={handleCreateTicket} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Catégorie</label>
                  <select
                    value={ticketForm.category}
                    onChange={(e) => setTicketForm({...ticketForm, category: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg"
                  >
                    <option value="question">Question</option>
                    <option value="technical">Technique</option>
                    <option value="billing">Facturation</option>
                    <option value="other">Autre</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sujet *</label>
                  <input
                    type="text"
                    required
                    value={ticketForm.subject}
                    onChange={(e) => setTicketForm({...ticketForm, subject: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg"
                    placeholder="Résumé de ta demande..."
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Message *</label>
                  <textarea
                    required
                    rows={4}
                    value={ticketForm.message}
                    onChange={(e) => setTicketForm({...ticketForm, message: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg"
                    placeholder="Décrivez ta demande en détail..."
                  />
                </div>
                
                <div className="flex gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowTicketModal(false)} className="flex-1">
                    Annuler
                  </Button>
                  <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700" data-testid="submit-ticket-btn">
                    <Send className="w-4 h-4 mr-2" />
                    Envoyer
                  </Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
